import { endpointRequest } from '../EndpointService/EndpointFunctions';

export default function createOrganizationService(jokiInstance, options = {}) {
    const serviceId = options.serviceId || 'OrganizationService';
    const joki = jokiInstance;

    const orgs = new Map();

    let crudRequestActive = 0;

    function handleJokiEvents(event) {
        if (event.to === serviceId) {
            switch (event.key) {
                case 'getState':
                    return Array.from(orgs.values());
                case 'get':
                    return get(event.body ? event.body : null);
                case 'getCrudList':
                    return get();
                case 'getFullCrudItem':
                    return crudGetItem(event.body.id);
                case 'getEmptyCrudItem':
                    return {
                        id: '',
                        name: '',
                        status: 'Active',
                        streetAddress: '',
                        city: '',
                        country: '',
                        metadata: '',
                        resellerId: '',
                        clientSegment: '',
                        parentId: null,
                    };
                case 'crudNewItem':
                    return crudNewOrganization(event.body);

                case 'crudUpdateItem':
                    return crudUpdateOrganization(event.body);
                case 'crudDeleteItem':
                    return crudDeleteOrganization(event.body);
                case 'getAvailableParents':
                    return findAvailableParents(event.body);
                default:
                    break;
            }
        }

        if (event.from === 'JOKI' && event.key === 'initialize') {
            init(event.body);
        }

        // in case for example organization filter changed
        // if (event.key === 'DataResetRequired') {
        //     resetData();
        // }
    }

    function init() {
        //console.log('Initialize :', serviceId, eventBody);
    }

    // For now the structure of 'organizations' end point in graphQL is not supporting getting a single organization to be loaded
    // function resetData() {
    //     orgs.clear();
    //     triggerServiceUpdate();
    //     get();
    // }

    function get(id) {
        if (!id) {
            loadSimpleList();
            return Array.from(orgs.values());
        }

        if (orgs.has(id)) {
            loadOrganizationDetails(id);
            return orgs.get(id);
        }

        return {
            error: `Organization ID ${id} is not available`,
        };
    }

    /**
     * Do not resolve until item has been loaded from the cloud.
     * @param {*} id - Organization Id to be loaded and returned
     */
    async function crudGetItem(id) {
        await loadSimpleList(id);
        if (orgs.has(id)) {
            return orgs.get(id);
        }
    }

    async function crudNewOrganization(itemData) {
        try {
            // Do not even make a call if we already have this ID in current memory
            if (orgs.has(itemData.id)) {
                return null;
            }

            const epOpts = {
                body: { ...itemData },
            };
            const res = await endpointRequest(joki, 'organization_new', epOpts);

            if (res === null) {
                return null;
            }

            await updateGroupsInPassport(itemData, 'ADD');

            reloadDataUntil(itemData.id);

            return res;
        } catch (err) {
            console.error('ERR', err);
            return err;
        }
    }

    async function updateGroupsInPassport(itemData, action) {
        try {
            const user = await joki
                .ask({
                    to: 'UserService',
                    key: 'getCurrentUser',
                })
                .then(user => {
                    if (user.UserService !== undefined) {
                        return user.UserService;
                    }
                });

            const groupIds = user && user.groups ? user.groups.map(g => g.Id) : [];

            let finalGroups = [];

            if (action === 'ADD') {
                finalGroups = [...groupIds, itemData.id];
            }
            if (action === 'REMOVE') {
                finalGroups = groupIds.filter(g => g !== itemData.id);
            }

            const updatedUser = {
                firstName: user.firstName,
                middleName: user.middleName,
                lastName: user.lastName,
                metadata: JSON.stringify({
                    groups: finalGroups,
                    modules: user.modules,
                }),
                roles: user.roles,
                scopeId: null, // If null, no change will be recorded in db.
            };

            const passportUpdateResult = await endpointRequest(joki, 'user_organization_update_passport', {
                body: { ...updatedUser },
            });

            if (passportUpdateResult != null && passportUpdateResult.message === 'Success') {
                joki.trigger({
                    from: serviceId,
                    key: 'updateUserInfo',
                    to: 'UserService',
                });
            }
        } catch (error) {
            console.error('ERR', error);
        }
    }

    function reloadDataUntil(targetId, testFn = null, calls = 1) {
        const loopMax = 5;
        if (!orgs.has(targetId) || (typeof testFn === 'function' && testFn(orgs.get(targetId)))) {
            crudRequestActive = crudRequestActive === 0 ? 1 : crudRequestActive;
            if (calls > loopMax) {
                crudRequestActive = 0;
                joki.trigger({
                    from: serviceId,
                    key: 'CrudStateUpdating',
                    body: false,
                });
                throw new Error(
                    `${serviceId}:reloadUntilExists: No id ${targetId} found after ${loopMax} retries. The Organization was not created or is taking way too long. Please check that connections are working and cloud services are up and running.`
                );
            }
            if (crudRequestActive === 1) {
                crudRequestActive = 2;
                joki.trigger({
                    from: serviceId,
                    key: 'CrudStateUpdating',
                    body: true,
                });
            }
            get();
            setTimeout(() => {
                reloadDataUntil(targetId, testFn, calls + 1);
            }, 1000 * calls);
        }
        crudRequestActive = 0;

        joki.trigger({
            from: serviceId,
            key: 'CrudStateUpdating',
            body: false,
        });
    }

    async function crudUpdateOrganization(itemData) {
        try {
            if (!orgs.has(itemData.id)) {
                throw new Error(`Cannot update an unknown Organization. Id ${itemData.id} is not unknown.`);
            }

            const dataWithoutRaw = { ...itemData };
            delete dataWithoutRaw.metadata;
            const epOpts = {
                body: {
                    ...dataWithoutRaw,
                },
                urlParse: {
                    $organizationId$: encodeURI(itemData.id),
                },
            };

            const originalObject = { ...orgs.get(itemData.id) };

            const res = await endpointRequest(joki, 'organization_update', epOpts);

            if (res === null) {
                console.warn(`${serviceId}:crudUpdateOrganization: Organization Id ${itemData.id} could not be updated as it does not exist in the cloud.`);
                return null;
            }
            //await updateOrganizationMetaData(itemData);

            reloadDataUntil(itemData.id, currentItem => {
                const origStr = Object.values(originalObject).join('|');
                const curStr = Object.values(currentItem).join('|');
                return curStr === origStr;
            });

            return res;
        } catch (err) {
            console.error(`${serviceId}:crudUpdateOrganization:ERROR`, err);
            return err;
        }
    }

    // async function updateOrganizationMetaData(itemData) {
    //     try {
    //         if (!orgs.has(itemData.id)) {
    //             throw new Error(`Cannot update an unknown Organization. Id ${itemData.id} is not unknown.`);
    //         }

    //         if (typeof itemData.metadata !== 'object' || typeof itemData.metadata.raw !== 'object' || itemData.metadata.raw === null) {
    //             return;
    //         }

    //         const epOpts = {
    //             body: {
    //                 ...itemData.metadata,
    //             },
    //             urlParse: {
    //                 $organizationId$: encodeURI(itemData.id),
    //             },
    //         };

    //         const res = await endpointRequest(joki, 'organization_metadata_update', epOpts);
    //         if (!res || res === null) {
    //             console.warn(
    //                 `${serviceId}:updateOrganizationMetaData: Organization Id ${itemData.id} metadata could not be updated as the organization does not exist in the cloud.`
    //             );
    //         }
    //         return res;
    //     } catch (err) {
    //         console.error(`${serviceId}:updateOrganizationMetaData:ERROR`, err);
    //         return err;
    //     }
    // }

    async function crudDeleteOrganization(itemData) {
        try {
            if (!orgs.has(itemData.id)) {
                throw new Error(`No organization with id ${itemData.id} found, cannot archive it.`);
            }

            const res = await endpointRequest(joki, 'organization_delete', {
                body: {
                    id: itemData.id,
                },
                urlParse: {
                    $organizationId$: encodeURI(itemData.id),
                },
            });

            await updateGroupsInPassport(itemData, 'REMOVE');

            orgs.delete(itemData.id);
            get();
            setTimeout(() => {
                get();
            }, 1000);

            return res;
        } catch (err) {
            console.error(`${serviceId}:crudDeleteOrganization:ERROR`, err);
            return err;
        }
    }

    function findAvailableParents(id) {
        if (orgs.size === 0) {
            loadSimpleList();
        }
        const availableParents = [];
        for (const [key, value] of orgs) {
            if (!id || id === '' || key !== id) {
                availableParents.push({ label: value.name || key, value: key });
            }
        }
        return availableParents;
    }

    async function loadSimpleList() {
        joki.trigger({
            from: serviceId,
            key: 'CrudStateUpdating',
            body: true,
        });
        try {
            const results = await endpointRequest(joki, 'organizations_list');
            if (results && results.organizations) {
                mergeResults(results.organizations);
                triggerServiceUpdate();
            } else {
                console.warn(`${serviceId}:loadSimpleList: Results for list query are invalid.\n`, results);
            }
        } catch (err) {
            console.error(`${serviceId}:loadSimpleList:\n`, err);
        }
        joki.trigger({
            from: serviceId,
            key: 'CrudStateUpdating',
            body: false,
        });
    }

    async function loadOrganizationDetails(id) {
        loadSimpleList();
        if (orgs.has(id)) {
            return orgs.get(id);
        }
        return {};
    }

    function mergeResults(resultArray) {
        resultArray.forEach(res => {
            const id = res.id;
            if (orgs.has(id)) {
                const newData = Object.assign({}, orgs.get(id), res);
                orgs.set(id, newData);
            } else {
                orgs.set(id, res);
            }
        });
    }

    function triggerServiceUpdate() {
        joki.serviceUpdate(serviceId, Array.from(orgs.values()), { serviceUpdate: true });
    }

    joki.addService({
        id: serviceId,
        fn: handleJokiEvents,
    });
}
